import { useEffect, useState, useMemo } from 'react';
import { useInput, useGetList, useGetMany } from 'react-admin';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { PROVIDER_ROLES_RESOURCE_NAME as PROVIDER_ROLES_RESOURCE } from '../../roles/provider.roles.dataProvider';
import logger from '../../utils/logger';
export const useProviderRoles = (record, rolesFieldName) => {
  const [orderedProviderRoles, setOrderedProviderRoles] = useState([]);
  
  const {
    field: { value, onChange }
  } = useInput({
    source: rolesFieldName,
    defaultValue: record?.[rolesFieldName] || []
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Debug log for initial data
  // logger.log('Record:', record);
  // logger.log('RolesFieldName:', rolesFieldName);
  // logger.log('Role IDs from record:', record?.[rolesFieldName]);

  const { data: getListData, isLoading: isLoadingList, error: listError } = useGetList(
    PROVIDER_ROLES_RESOURCE,
    { 
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'id', order: 'ASC' }
    }
  );

  // Debug log for list data
  // logger.log('GetList Data:', getListData);
  // logger.log('GetList Loading:', isLoadingList);
  // logger.log('GetList Error:', listError);

  // Use value from the form field instead of record directly
  const roleIds = value;
  const validRoleIds = useMemo(() => {
    const ids = roleIds || [];
    return Array.isArray(ids) ? ids : [];
  }, [roleIds]);

  // Debug log for role IDs
  // logger.log('Valid Role IDs:', validRoleIds);

  const { data: getManyData, isLoading: isLoadingMany, error: manyError } = useGetMany(
    PROVIDER_ROLES_RESOURCE,
    { ids: validRoleIds }
  );

  // Debug log for getManyData
  // logger.log('GetMany Data:', getManyData);
  // logger.log('GetMany Loading:', isLoadingMany);
  // logger.log('GetMany Error:', manyError);

  useEffect(() => {
    if (!getManyData) return;

    const roleMap = getManyData.reduce((acc, role) => {
      acc[role.id] = role;
      return acc;
    }, {});
    
    // logger.log('Role Map:', roleMap);
    
    const filteredRoles = validRoleIds.map(roleId => {
      const role = roleMap[roleId];
      if (!role) {
        // logger.log('Missing role for ID:', roleId);
        return {
          id: roleId,
          name: "Deleted Role",
          isDeleted: true,
        };
      }
      return { ...role, isDeleted: false };
    });
    
    // logger.log('Filtered Roles:', filteredRoles);
    setOrderedProviderRoles(filteredRoles);
  }, [getManyData, validRoleIds]);

  const setOrderedRoles = (roles) => {
    const newRoleIds = roles.map(role => role.id);
    onChange(newRoleIds);
    setOrderedProviderRoles(roles);
  };

  return {
    orderedProviderRoles,
    setOrderedRoles,
    sensors,
    getListData,
    isLoading: isLoadingList || isLoadingMany,
    error: listError || manyError
  };
}; 